// @flow
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import useClientId from 'common/hooks/useClientId';
import { plan as planApi } from 'platform/api';
import {
    Box,
    Flex,
    Stack,
    Position,
    Button,
    Heading,
    useToast,
    Icon
} from '@locus-taxy/neo-ui';

import { rankWith, uiTypeIs } from '@jsonforms/core';

import useLayoutProps from '../util/useLayoutProps';
import { parsePlanLink } from 'platform/settings/form/utils/parsePlanLink';
import {
    useClientSettings,
    useUpdateClientSettings
} from 'platform/settings/hooks/useClientSettings';
import {
    useBusinessUnitSettings,
    useUpdateBusinessUnitSettings
} from 'platform/settings/hooks/useBusinessUnit';
import JsonForm from 'platform/settings/form/JsonForm';
import { toTranslationKey } from 'platform/settings/utils';

const product = 'platform';

type Props = {};

const SetDefaultVAEConfigLayoutRenderer = (props: Props): React.Node => {
    const clientId = useClientId();
    const { uischema } = useLayoutProps(props);
    const [{ value, errors }, setData] = React.useState({
        errors: null,
        value: { context: { toTranslationKey }, clientSettings: {} }
    });

    const handleChange = (value, errors) => {
        setData({ value, errors });
    };

    const {
        t,
        i18n: { language }
    } = useTranslation('platform');

    const i18n = React.useMemo(() => {
        return {
            locale: language,
            translate: (key, origKey) => {
                const _key = toTranslationKey(product, key);

                if (key.endsWith('label') || key.endsWith('description')) {
                    return t(_key);
                }
                return origKey;
            }
        };
    }, [language, t]);

    const { showToast } = useToast();

    const clientSettings = useClientSettings();
    const businessUnitSettings = useBusinessUnitSettings();
    const updateClientSettings = useUpdateClientSettings();
    const updateBusinessUnitSettings = useUpdateBusinessUnitSettings();

    const onSave = async () => {
        const { batchId, planId } = parsePlanLink(
            value.clientSettings?.setDefaultVaeConfig?.planLink
        );

        let vaeConfigs = {};
        if (batchId && planId) {
            const getPlanData = await planApi.fetch({
                clientId,
                batchId,
                planId
            });
            vaeConfigs = getPlanData?.serializedVaeConfiguration
                ? JSON.parse(getPlanData.serializedVaeConfiguration)
                : {};

            if (vaeConfigs.timeBinningConfig) {
                vaeConfigs.timeBinningConfig.bins = [];
            }
        }

        if (
            value.clientSettings?.setDefaultVaeConfig?.level?.key ===
            'CLIENT_LEVEL'
        ) {
            const parsedData = clientSettings.data
                ? JSON.parse(clientSettings.data.payload)
                : {};
            const payload = {
                ...parsedData,
                vaeConfiguration: {
                    ...parsedData.vaeConfiguration,
                    ...vaeConfigs
                }
            };
            const requestPayload = {
                payload: JSON.stringify(payload),
                reason: 'Setting VAE Configuration from internal dashboard'
            };
            updateClientSettings.mutate(requestPayload, {
                onSuccess: () => {
                    showToast({
                        title:
                            'Set Default VAE Configuration for client has been updated successfully.',
                        palette: 'success',
                        icon: (
                            <Icon
                                icon="check_circle"
                                size="xLarge"
                                color="green.500"
                            />
                        )
                    });
                },
                onError: error => {
                    showToast({
                        title: error.message,
                        palette: 'danger',
                        icon: (
                            <Icon
                                icon="cross_circle"
                                size="xLarge"
                                color="red.600"
                            />
                        )
                    });
                }
            });
        }

        if (
            value.clientSettings?.setDefaultVaeConfig?.level?.key ===
            'BUSINESS_UNIT_LEVEL'
        ) {
            const businessUnitId =
                value.clientSettings?.setDefaultVaeConfig?.businessUnitId;
            const businessUnitData = await businessUnitSettings.mutateAsync({
                businessUnitId
            });
            const parsedData = JSON.parse(businessUnitData.payload);

            const payload = {
                ...parsedData,
                vaeConfiguration: {
                    ...parsedData.vaeConfiguration,
                    ...vaeConfigs
                }
            };

            const requestPayload = {
                payload: JSON.stringify(payload),
                reason: 'Updating VAE configs from internal dashboard'
            };

            updateBusinessUnitSettings.mutate(
                {
                    businessUnitId,
                    requestPayload
                },
                {
                    onSuccess: () => {
                        showToast({
                            title: `Set Default VAE Configuration for ${businessUnitId} has been updated successfully.`,
                            palette: 'success',
                            icon: (
                                <Icon
                                    icon="check_circle"
                                    size="xLarge"
                                    color="green.500"
                                />
                            )
                        });
                    },
                    onError: error => {
                        showToast({
                            title: error.message,
                            palette: 'danger',
                            icon: (
                                <Icon
                                    icon="cross_circle"
                                    size="xLarge"
                                    color="red.600"
                                />
                            )
                        });
                    }
                }
            );
        }
    };

    return (
        <Stack space="medium">
            <Heading size="large" color="grey.900">
                {t(uischema.label)}
            </Heading>
            <Box>
                <Stack space={uischema.options?.space || 'medium'}>
                    <JsonForm
                        metadata={uischema.options.layout}
                        value={value}
                        i18n={i18n}
                        onChange={handleChange}
                    />
                    <Position
                        position="absolute"
                        bottom="0"
                        right="0"
                        width="100%"
                    >
                        <Flex width="100%" p="4" flexDirection="row-reverse">
                            <Button
                                appearance="solid"
                                palette="primary"
                                onClick={() => {
                                    onSave();
                                }}
                                disabled={Boolean(errors && errors.length > 0)}
                            >
                                {t('save')}
                            </Button>
                        </Flex>
                    </Position>
                </Stack>
            </Box>
        </Stack>
    );
};

export default SetDefaultVAEConfigLayoutRenderer;

export const setDefaultVAEConfigLayoutTester = rankWith(
    1,
    uiTypeIs('SetDefaultVAEConfigLayout')
);
